import { useState } from "react";

import { useTheme } from "@mui/material/styles";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import Switch from "@mui/material/Switch";

import { sanitizePhoneNumber } from "core/strings";

import Box from "ui/atom/Box";
import Form from "ui/atom/Form";
import Link from "ui/atom/Link";

import Alert from "ui/base/Alert";
import Button from "ui/base/Button";
import Input from "ui/base/Input";
import Title from "ui/base/Title";

import Logo from "ui/Logo";
import Page from "ui/Page";

import { useAuth, useAuthValidate, useLogin, useSignup, useValidate } from "features/auth/fabric";


Auth.Input = (props) => {
	const auth = useAuthValidate();

	const setEmail = (e) => auth.setEmail(e.target.value);
	const setPhone = (e) => auth.setPhone(e.target.value);

	if (auth.isEmail()) return <Input.Email value={auth.email} onChange={setEmail} required errors={auth.error?.errors?.email} {...props} />;;
	return <Input.Phone country={auth.country} value={auth.phone} onChange={setPhone} required errors={auth.error?.errors?.phone} {...props} />;
};

Auth.Type = () => {
	const auth = useLogin();

	return (
		<Box sx={{ alignItems: "center" }}>
			<Typography color={!auth.isEmail() ? "text.disabled" : "text.primary"}>Email</Typography>
			<Switch checked={!auth.isEmail()} onChange={auth.toggleType} disabled={auth.isLoginPending} />
			<Typography color={auth.isEmail() ? "text.disabled" : "text.primary"}>Phone</Typography>
		</Box>
	);
};

Auth.Sso = () => {
	const auth = useAuth();
	const theme = useTheme();
	const platforms = ["apple", "microsoft", "facebook", "google"];
	const icon = auth.isEmail() ? "call" : "mail";
	const title = auth.isEmail() ? "Choose a phone number" : "Choose an email";

	const style = {
		button: {
			backgroundColor: theme.palette.light.main,
			borderRadius: "8px",
			transition: "all 200ms ease",
			":hover": { backgroundColor: theme.palette.light.dark }
		}
	};

	return (
		<Box gap={1}>
			<Button.Icon icon={icon} size={22} color="black" onClick={auth.toggleType} title={title} sx={style.button} />
			{platforms.map((p, i) => <Button.Icon icon={p} size={22} sx={style.button} key={i} />)}
		</Box>
	);
};

Auth.Login = () => {
	const auth = useLogin();
	const theme = useTheme();
	const style = {
		form: { gap: theme.spacing(3) },
		actions: { alignItems: "center" },
		signin: { alignSelf: "center" },
		action: { maxWidth: "300px" },
		input: { alignItems: "flex-start" },
		button: {
			backgroundColor: theme.palette.light.main,
			width: "fit-content",
			borderRadius: "8px",
			transition: "all 200ms ease",
			":hover": {
				backgroundColor: theme.palette.light.dark
			}
		}
	};

	return (
		<Auth>
			<Form sx={style.form} onSubmit={auth.mutation.mutate}>
				<Alert.Error errors={auth.mutation.error?.errors?.[""]} />

				<Box.Column gap={1.25} sx={{ alignItems: "center" }}>
					<Auth.Type />
					<Auth.Input asterisk={false} />
				</Box.Column>
				<Box.Column gap={1.25} sx={style.actions}>
					<Button type="submit" variant="contained" color="tertiary" loading={auth.mutation.isPending} fullWidth sx={style.action}>Login / Signup</Button>
				</Box.Column>
			</Form>
		</Auth>
	);
};


const Section = ({ title, children }) => (
	<Box.Column gap={3}>
		<Typography variant="h4">{title}</Typography>
		<Box.Column gap={2}>
			{children}
		</Box.Column>
	</Box.Column>
);

Auth.SignUp = () => {
	const auth = useSignup();
	const [phone, setPhone] = useState("");

	const theme = useTheme();
	const style = {
		form: {
			backgroundColor: theme.palette.background.form,
			padding: theme.spacing(5.5, 2.75),
			borderRadius: "12px",
			gap: theme.spacing(3)
		},
		terms: {
			alignItems: "center"
		},
		legal: {
			"a": {
				color: theme.palette.link.main,
				textDecoration: "underline",
				transition: "all 200ms ease",
				":hover": { color: theme.palette.link.dark }
			}
		}
	};

	const submit = async (e) => {
		e.preventDefault();
		const formData = new FormData(e.target);
		const data = Object.fromEntries(formData.entries());

		const body = {
			name: data.name.trim()
		};

		if (data.nickname) body.nickname = data.nickname.trim();
		if (data.phone) body.secondary = sanitizePhoneNumber(`${auth.country}${data.phone}`);
		else if (data.email) body.secondary = data.email.trim();

		await auth.mutation.mutate(body);
	};

	return (
		<Auth>
			<Title title="Create an Account" subtitle="Join Subly to unlock the news, your way." />
			<Form sx={style.form} onSubmit={submit}>
				<Alert.Error errors={auth.mutation.error?.errors?.[""]} />
				<Section title="Personal Information">
					<Input name="name" label="Full Name" required />
					<Input name="nickname" label="Nickname" placeholder="How your name appears on your profile" info="This name can’t be used for login." />
				</Section>

				<Section title="Account Security">
					<Auth.Input disabled />
					{auth.isEmail()
						? <Input.Phone value={phone} onChange={e => setPhone(e.target.value)} country={auth.country} info="Can’t be used for login until verified in account settings." errors={auth.mutation.error?.errors?.phone} />
						: <Input.Email info="Can’t be used for login until verified in account settings." errors={auth.mutation.error?.errors?.email} />}
				</Section>

				<Section title="Sign-in code">
					<Input.Otp value={auth.code} setValue={auth.setCode} destination={auth.email || auth.phone} disabled={auth.mutation.isPending} />
				</Section>

				<Section title="Terms of Use">
					<Box sx={style.terms}>
						<Checkbox checked={auth.check} onChange={auth.toggleCheck} disabled={auth.mutation.isPending} />
						<Typography component="span" sx={style.legal}>
							* I agree to the <Link to="/legal/tou.html" tab>Subly Terms & Conditions</Link> and have read the <Link to="/legal/privacy.html" tab>Subly Privacy Policy</Link>.
						</Typography>
					</Box>
				</Section>
				<Button type="submit" variant="contained" color="tertiary" loading={auth.mutation.isPending} disabled={auth.disabled || auth.mutation.isPending || auth.mutation.isSuccess}>Create Account</Button>
			</Form>
		</Auth>
	);
};

Auth.Confirm = () => {
	const auth = useValidate();

	const style = {
		content: { width: "fit-content", mx: "auto" },
		link: { alignSelf: "center", textAlign: "center" }
	};

	return (
		<Auth>
			<Box.Column gap={2} sx={style.content}>
				<Alert.Error errors={auth.mutation.error?.errors?.[""]} />
				<Input.Otp value={auth.code} setValue={auth.setCode} destination={auth.account} onSubmit={auth.mutation.mutate} resend={auth.resend} disabled={auth.mutation.isPending} />
				<Box.Column gap={1}>
					<Button variant="contained" color="tertiary" onClick={auth.mutation.mutate} loading={auth.mutation.isPending || auth.mutation.isSuccess}>Submit</Button>
					<Typography component="span" color="text.primary" sx={style.link}>Wrong {auth.type}? <Link to="/auth/login" color="tertiary.main" disabled={auth.mutation.isPending}>Try Again</Link></Typography>
				</Box.Column>
			</Box.Column>
		</Auth>
	);
};

export default function Auth({ children }) {
	const auth = useAuthValidate();
	const theme = useTheme();
	const errors = auth.error?.errors;

	const style = {
		root: { gap: theme.spacing(3) },
		header: { alignItems: "center" }
	};

	return (
		<Page maxWidth="dark" sx={style.root}>
			<Page.Header gap={2} sx={style.header}>
				<Link to="/">
					<Logo.Full size={210} />
				</Link>
			</Page.Header>
			<Page.Body sx={style.root}>
				<Alert.Error errors={errors?.[""]} />
				{children}
			</Page.Body>
		</Page>
	);
}